import { create } from 'zustand';
import { invokeTauri, isTauriRuntime, listenTauri, runCanonBundleThroughTauri } from '@/engine/tauriBridge';
import { mirrorNow } from '@/engine/mirror';
import { buildCanonBundle } from '@/engine/canonBundle';
import { saveEngineRun } from '@/db/engineRuns';
import { checksumOf } from '@/lib/hash';
import { appendConsoleEvent, clearConsoleEvents, listConsoleEvents } from '@/db/consoleEvents';
import { decideProposalBundle, getProposalBundle, saveProposalBundle } from '@/db/proposalBundles';
import { incomingProposalSchema } from '@/lib/schemas/proposal.schema';
import { applyProposalDelta, applyProposalPatches, applySceneProposal } from '@/engine/applyProposal';
import { useCodexStore } from '@/stores/codexStore';
import type { ConsoleEventRecord, ProposalBundleRecord, ProposalDeltaOp, ProposalFix, ProposalRippleItem } from '@/db/codexDB';
import { newId } from '@/lib/id';

/**
 * 콘솔 세션 store(재설계 R3) — 레일 콘솔 탭과 /console 풀스크린이 **같은 세션**을 렌더한다.
 * 전송 시 휘발(setEvents([])) 금지: 이벤트는 Dexie consoleEvents에 영속, 재시작 후 복원.
 * 에이전트는 read/propose만(P0 화이트리스트) — canon 자동확정 0은 백엔드 경계가 보증.
 */
interface ConsoleState {
  events: ConsoleEventRecord[];
  running: boolean;
  hydratedFor: string | null;
  /** 글로벌 콘솔 도크(⌘J, K2) — 수동 화면 위에서 같은 세션(홈에선 컴포저가 본체). */
  dockOpen: boolean;
  toggleDock: () => void;
  closeDock: () => void;
  /** 홈 우측 artifact 패널(F0) — 타임라인 카드의 상세/산출물. */
  artifactEventId: string | null;
  openArtifact: (eventId: string) => void;
  closeArtifact: () => void;
  hydrate: (projectId: string) => Promise<void>;
  send: (prompt: string, opts?: { preamble?: string; contextNote?: string }) => Promise<void>;
  cancel: () => Promise<void>;
  clear: () => Promise<void>;
  /** K1 승인 큐 — 작가의 결정(유일한 커밋 경로). 수락 시 Δ를 Dexie 정본에 적용 + approvalId 감사. */
  decideProposal: (proposalId: string, accepted: boolean) => Promise<void>;
}

let streamBound = false;
let activeProjectId = '';
/** K5-b 세션 resume — 사이드카가 알려준 SDK session_id를 프로젝트별 영속, 다음 전송에 이어붙인다. */
let agentSid: string | null = null;
const sidKey = (projectId: string) => `loom:agent-sid:${projectId}`;

function record(partial: Omit<ConsoleEventRecord, 'id' | 'projectId' | 'at'>): ConsoleEventRecord {
  return { id: newId('cev'), projectId: activeProjectId, at: Date.now(), ...partial };
}

export const useConsoleStore = create<ConsoleState>((set, get) => {
  const append = (ev: ConsoleEventRecord) => {
    set((s) => ({ events: [...s.events, ev] }));
    if (ev.projectId) void appendConsoleEvent(ev).catch(() => undefined); // 영속 실패는 세션을 막지 않음
  };

  /** 에이전트의 propose_change/scene/patch → 승인 큐(Dexie)에 proposed로 적재 + 타임라인 카드 이벤트.
   *  K5-d: 도착은 신뢰 경계 — zod로 형을 강제하고, 불량은 조용히 버리지 않고 반려 이벤트로 남긴다. */
  const handleProposal = async (raw: unknown) => {
    const parsed = incomingProposalSchema.safeParse(raw);
    if (!parsed.success) {
      console.error('[console] 제안 형식 불량 — 반려', parsed.error.issues);
      append(record({ role: 'system', type: 'system', text: `제안 반려 — 형식 불량(${parsed.error.issues[0]?.message ?? 'schema'})` }));
      return;
    }
    const p = parsed.data;
    const kind: ProposalBundleRecord['kind'] = p.kind ?? (p.scene ? 'scene' : p.patches?.length ? 'patch' : 'delta');
    const scene = p.scene;
    const rec: ProposalBundleRecord = {
      id: newId('prop'),
      projectId: activeProjectId,
      createdAt: Date.now(),
      status: 'proposed',
      kind,
      title: p.title,
      ...(p.intent ? { intent: p.intent } : {}),
      ...(p.patches?.length ? { patches: p.patches } : {}),
      delta: (p.delta ?? []) as ProposalDeltaOp[],
      ripple: Array.isArray(p.ripple) ? (p.ripple as ProposalRippleItem[]) : [],
      fixes: Array.isArray(p.fixes) ? (p.fixes as ProposalFix[]) : [],
      ...(kind === 'scene' && scene ? { scene } : {}),
    };
    try {
      await saveProposalBundle(rec);
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      append(record({ role: 'system', type: 'error', text: `제안 저장 실패 — 승인 큐에 적재하지 못했다: ${detail}` }));
      return;
    }
    append(record({ role: 'agent', type: 'proposal', text: p.title, proposalId: rec.id }));
  };

  /** Tauri 'agent-stream'/'agent-done' 구독 — 앱 수명당 1회(두 표면이 공유). */
  const bindStream = async () => {
    if (streamBound || !isTauriRuntime()) return;
    streamBound = true;
    await listenTauri<{ type?: string; text?: string; tool?: string; ok?: boolean; proposal?: unknown; sessionId?: string }>('agent-stream', (e) => {
      const p = e.payload ?? {};
      if (p.type === 'proposal') {
        void handleProposal(p.proposal); // K1: 제안은 텍스트가 아니라 승인 큐 레코드로
        return;
      }
      if (p.type === 'session') {
        // K5-b: 타임라인엔 안 싣고 영속만 — 다음 전송이 이 대화를 이어받는다(resume).
        if (typeof p.sessionId === 'string' && p.sessionId) {
          agentSid = p.sessionId;
          if (activeProjectId) localStorage.setItem(sidKey(activeProjectId), p.sessionId);
        }
        return;
      }
      const type = (p.type as ConsoleEventRecord['type']) ?? 'raw';
      append(
        record({
          role: type === 'system' ? 'system' : 'agent',
          type,
          ...(p.text !== undefined ? { text: p.text } : {}),
          ...(p.tool !== undefined ? { tool: p.tool } : {}),
          ...(p.ok !== undefined ? { ok: p.ok } : {}),
        }),
      );
    });
    await listenTauri('agent-done', () => set({ running: false }));
  };

  return {
    events: [],
    running: false,
    hydratedFor: null,
    dockOpen: false,
    toggleDock: () => set((s) => ({ dockOpen: !s.dockOpen })),
    closeDock: () => set({ dockOpen: false }),
    artifactEventId: null,
    openArtifact: (eventId) => set({ artifactEventId: eventId }),
    closeArtifact: () => set({ artifactEventId: null }),

    hydrate: async (projectId) => {
      activeProjectId = projectId;
      agentSid = localStorage.getItem(sidKey(projectId)); // K5-b: 재시작 후에도 대화가 이어진다
      await bindStream();
      if (get().hydratedFor === projectId) return;
      const events = await listConsoleEvents(projectId).catch(() => [] as ConsoleEventRecord[]);
      set({ events, hydratedFor: projectId });
    },

    send: async (prompt, opts) => {
      const text = prompt.trim();
      if (!text || get().running || !isTauriRuntime()) return;
      append(record({ role: 'user', type: 'prompt', text, ...(opts?.contextNote ? { contextNote: opts.contextNote } : {}) }));
      set({ running: true });
      try {
        const mirrored = await mirrorNow().catch(() => false); // K5-f flush 핸드셰이크 — 방금 쓴 canon을 커널이 본다
        if (!mirrored) {
          append(record({ role: 'system', type: 'error', text: '커널 미러 갱신 실패 — 최신 canon을 보장할 수 없어 전송을 중단했다. 잠시 뒤 다시 시도하라.' }));
          set({ running: false });
          return;
        }
        const full = opts?.preamble ? `${opts.preamble}\n\n[지시]\n${text}` : text;
        await invokeTauri('agent_run', { prompt: full, resumeSid: agentSid });
      } catch (err) {
        append(record({ role: 'system', type: 'error', text: err instanceof Error ? err.message : String(err) }));
        set({ running: false });
      }
    },

    cancel: async () => {
      await invokeTauri('agent_cancel').catch(() => undefined);
      append(record({ role: 'system', type: 'system', text: '취소됨' }));
      set({ running: false });
    },

    clear: async () => {
      if (activeProjectId) {
        await clearConsoleEvents(activeProjectId).catch(() => undefined);
        localStorage.removeItem(sidKey(activeProjectId)); // 세션 비우기 = 대화 기억도 새로
      }
      agentSid = null;
      set({ events: [] });
    },

    decideProposal: async (proposalId, accepted) => {
      const rec = await getProposalBundle(proposalId);
      if (!rec || rec.status !== 'proposed') return;
      if (accepted) {
        const cx = useCodexStore.getState();
        const ctx = {
          project: cx.project,
          entities: cx.entities,
          patchProjectMeta: cx.patchProjectMeta,
          patchEntity: cx.patchEntity,
          // K5-d: 패치 집행은 기존 store 액션으로(멘션 흡수·중복 검사·undo 모두 기존 배관)
          links: cx.links,
          patchField: cx.patchField,
          addLink: (input: Parameters<typeof cx.addLink>[0]) => cx.addLink(input),
          removeLink: cx.removeLink,
        };
        const notes =
          rec.kind === 'scene' && rec.scene
            ? applySceneProposal(rec.scene, ctx)
            : rec.kind === 'patch'
              ? applyProposalPatches(rec.patches ?? [], ctx)
              : applyProposalDelta(rec.delta, ctx);
        await decideProposalBundle(proposalId, 'accepted', { approvalId: newId('approval'), appliedNotes: notes });
        append(record({ role: 'system', type: 'system', text: `제안 수락 — ${rec.title} (${notes.length}건 적용)` }));
        // ★K4-b 능동 감지: 커밋(수락)이 곧 새 Δ — 즉시 결정론 재검증해 커널이 먼저 보고한다
        // (감지=자동·무과금, 제안 생성=1클릭, 확정=작가). 실패는 보고만 막고 수락은 유효.
        if (isTauriRuntime()) {
          try {
            const fresh = useCodexStore.getState();
            if (fresh.project) {
              const bundle = buildCanonBundle({
                project: fresh.project,
                entities: Object.values(fresh.entities),
                links: Object.values(fresh.links),
              });
              const report = await runCanonBundleThroughTauri(bundle);
              await saveEngineRun(report, checksumOf(bundle)).catch(() => undefined);
              const hard = report.violations.filter((v) => v.hard).length;
              append(
                record({
                  role: 'agent',
                  type: 'system',
                  text:
                    hard === 0
                      ? `재파급 검증 — 세계와 충돌 0. 수락이 정합하게 닫혔다.`
                      : `재파급 검증 — 세계와 충돌 ${hard}건 남음(수리 제안 ${report.proposals.length}). 추가 조치가 필요하면 말하라.`,
                }),
              );
            }
          } catch {
            append(record({ role: 'system', type: 'system', text: '재파급 검증 실패 — 수락은 유효(다음 자동 검증이 잡는다)' }));
          }
        }
      } else {
        await decideProposalBundle(proposalId, 'rejected');
        append(record({ role: 'system', type: 'system', text: `제안 빼기 — ${rec.title}` }));
      }
    },
  };
});
